import {
  groupCoursesByTerm,
  collectTransferCourses,
  computeSummary,
  TranscriptCourseEnrollment,
  TranscriptTermGPA,
  TranscriptConductCase,
} from '../utils';
import TermTable from './TermTable';
import TransferTable from './TransferTable';
import CumulativeTable from './CumulativeTable';
import TranscriptSignature from './TranscriptSignature';
import OfficialTranscriptInfoPage from './OfficialTranscriptInfoPage';
import TranscriptHeader from './TranscriptHeader';

export type TranscriptMode = 'academic' | 'official';

export interface TranscriptProgramEnrollment {
  status?: string | null;
  graduatedAt?: unknown;
  program: {
    name: string;
  };
}

export interface TranscriptUser {
  sandId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  primaryEmail: string;
  countryOfOrigin?: string | null;
  birthDate?: unknown;
}

interface Props {
  user: TranscriptUser;
  programEnrollments: TranscriptProgramEnrollment[];
  courseEnrollments: TranscriptCourseEnrollment[];
  creditsByCourseDef: Map<string, number>;
  cumulativeGPA: number | null;
  termGPAs: TranscriptTermGPA[];
  conductCases?: TranscriptConductCase[];
  mode?: TranscriptMode;
  /**
   * Public verification URL. When provided in official mode, a QR code linking
   * to it is shown so a recipient can confirm the transcript's authenticity.
   */
  verifyUrl?: string;
  /**
   * When rendering a frozen (issued) transcript, the date it was issued.
   * Shown as the Granted/Issued-On date instead of the viewing date.
   */
  issuedAt?: Date;
  /** When the secure link expires; captions the QR code when known. */
  expiresAt?: Date;
  /** Pre-resolved address label for server-side PDF rendering (see header). */
  resolvedAddress?: string | null;
}

const formatGPA = (gpa: number | null | undefined): string => {
  if (gpa == null) return '—';
  return gpa.toFixed(2);
};

const TranscriptView = ({
  user,
  programEnrollments,
  courseEnrollments,
  creditsByCourseDef,
  cumulativeGPA,
  termGPAs,
  conductCases,
  mode = 'academic',
  verifyUrl,
  issuedAt,
  expiresAt,
  resolvedAddress,
}: Props) => {
  const isOfficial = mode === 'official';
  const headingText = isOfficial ? 'Official Transcript' : 'Academic Record';
  // Academic standing and conduct cases are shown only on the in-app
  // (unofficial) academic record — never on the official transcript. Withheld
  // from the grouping too, so no disciplinary data reaches the official render.
  const terms = groupCoursesByTerm(
    courseEnrollments,
    creditsByCourseDef,
    termGPAs,
    isOfficial ? undefined : conductCases
  );
  const transferCourses = collectTransferCourses(
    courseEnrollments,
    creditsByCourseDef
  );
  const transferEarnedCredits = transferCourses.reduce(
    (sum, c) => sum + c.earnedCredits,
    0
  );
  const summary = computeSummary(terms, cumulativeGPA);
  const semesterGPAs = terms.map((term) => formatGPA(term.gpa));
  const cumulativeGPAs = terms.map((term) => formatGPA(term.cumulativeGPA));

  return (
    <div className="bg-background rounded-xl px-4 md:px-8 pt-5 md:pt-7 pb-6 md:pb-8 flex flex-col gap-6 md:gap-8 items-center shadow-md print:px-0 print:shadow-none">
      <TranscriptHeader
        user={user}
        programEnrollments={programEnrollments}
        headingText={headingText}
        verifyUrl={isOfficial ? verifyUrl : undefined}
        expiresAt={expiresAt}
        documentDate={issuedAt}
        resolvedAddress={resolvedAddress}
      />

      {transferCourses.length > 0 && (
        <TransferTable courses={transferCourses} />
      )}

      {terms.map((term, i) => (
        <TermTable
          key={term.term}
          term={term}
          semesterGPA={semesterGPAs[i]}
          cumulativeGPA={cumulativeGPAs[i]}
          showStandingConduct={!isOfficial}
        />
      ))}

      <CumulativeTable
        summary={summary}
        transferEarnedCredits={transferEarnedCredits}
      />

      {isOfficial && <TranscriptSignature issuedAt={issuedAt} />}

      {isOfficial && <OfficialTranscriptInfoPage />}
    </div>
  );
};

export default TranscriptView;
